import React from "react";
import FadeReveal from "./FadeReveal";

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 17L17 7M17 7H8M17 7v9" />
  </svg>
);

const projectItems = [
  {
    title: "FloorWars — Campus Territory Game",
    description:
      "An Android campus territory game in development, built with a teammate. Students claim buildings for their faculty by taking the stairs: a background service fuses barometer and step-detector data to tell stair climbs from elevator rides and shaking, with no hardware in the buildings. Backed by a FastAPI/PostGIS server with live standings, QR and geofence check-in, an admin panel, and layered anti-cheat.",
    tags: ["React Native", "Kotlin", "FastAPI", "PostgreSQL", "PostGIS", "Docker"],
    linkLabel: "View FloorWars on GitHub",
    sourceUrl: "https://github.com/AlexandruCL/FloorWars",
  },
  {
    title: "LSFMT — Student Association Platform",
    image: "/lsfmt-black.svg",
    alt: "LSFMT logo",
    description:
      "An ongoing full-stack web platform for a student association, set to go live in the near future. It features a public-facing site (articles, programs, representatives, FAQ) and a secured admin panel where authenticated admins can create and edit articles with a rich-text editor, manage programs and representatives, upload media and PDFs, and reposition cover images.",
    tags: ["React", "TypeScript", "Supabase", "Vite", "RLS"],
    sourceUrl: "https://lsfmt.vercel.app",
    linkLabel: "Live site",
    githubLabel: "Visit the LSFMT live site",
    isLogo: true,
  },
  {
    title: "Water Quality Monitor",
    image: "/water_quality_iot_projectjpg.jpg",
    alt: "Water quality monitoring dashboard showing real-time sensor readings",
    description:
      "End-to-end IoT system: Raspberry Pi sensors stream water readings (pH, TDS, turbidity, temperature) to AWS over MQTT, processed by Lambda, stored in Django/PostgreSQL, and displayed on a live dashboard. An LSTM Autoencoder detects anomalies with a two-layer ML-gated alerting engine.",
    tags: ["IoT", "Raspberry Pi", "AWS", "Django", "LSTM", "ONNX"],
    sourceUrl: "https://github.com/patric1304/Water-quality-IOT-project",
    githubLabel: "View Water Quality Monitor on GitHub",
  },
    {
    title: "Duty Discord Bot",
    image: "/discord.avif",
    alt: "Duty Discord bot command interface preview",
    description:
      "A Discord bot for time tracking, attendance, and weekly reporting with role-based access and exportable performance reports.",
    tags: ["Python", "SQLite", "Automation"],
    sourceUrl: "https://github.com/AlexandruCL/Timestamps-Bot",
    githubLabel: "View Duty Bot on GitHub",
  },
  {
    title: "iTECify",
    image: "/iTECify.png",
    alt: "iTECify collaborative coding platform interface",
    description:
      "Real-time collaborative coding platform where developers can work together live, organise project files, run code securely in Docker sandboxes, and get AI-assisted coding support.",
    tags: ["React", "Fastify", "Node.js", "Docker", "Supabase"],
    sourceUrl: "https://github.com/AlexandruCL/iTEC_2026",
    githubLabel: "View iTECify on GitHub",
  },
  {
    title: "Echoes — Digital Heritage Trail",
    image: "/echoes.png",
    alt: "Echoes heritage discovery app map view",
    description:
      "A GPS-powered heritage discovery app built for a hackathon, enabling users to explore Timișoara's cultural landmarks through geo-located audio stories, gamification, and social features.",
    tags: ["React", "Supabase", "Node.js", "PWA"],
    sourceUrl: "https://github.com/ionutT77/Echoes-Digital-Heritage-Trail",
    githubLabel: "View Echoes on GitHub",
  },
  {
    title: "E-Commerce Platform",
    image: "/profudevin.png",
    alt: "Wine e-commerce store product listing page",
    description:
      "A full-stack Django wine store with authentication, social login, cart and checkout flow, and transactional email notifications.",
    tags: ["Django", "JavaScript", "SQL", "Tailwind"],
    sourceUrl: "https://github.com/AlexandruCL/ProiectProfuDeVin",
    githubLabel: "View E-Commerce Platform on GitHub",
  },
];

const Projects = () => {
  return (
    <section id="projects">
      <div className="shell">
        <div className="section-head">
          <FadeReveal direction="up">
            <span className="eyebrow">Selected work</span>
            <h2>Things I've built</h2>
            <p className="lead">
              A handful of projects that map my learning journey — spanning IoT,
              real-time systems, cloud, and full-stack web.
            </p>
          </FadeReveal>
        </div>

        <FadeReveal direction="up" delay={0.05}>
          <div className="projects-list">
            {projectItems.map((project, i) => (
              <article className="project-row" key={project.title}>
                <span className="project-num" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>

                <div className="project-main">
                  <h3>{project.title}</h3>
                  <p className="project-desc">{project.description}</p>

                  <div className="project-tags" aria-label="Technologies used">
                    {project.tags.map((tag) => (
                      <span key={`${project.title}-${tag}`} className="project-tag">{tag}</span>
                    ))}
                  </div>

                  <div className="project-actions">
                    {project.sourceUrl ? (
                      <a
                        href={project.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link primary"
                        aria-label={project.githubLabel}
                      >
                        {project.linkLabel || "Source"} <ArrowIcon />
                      </a>
                    ) : (
                      <span className="project-link primary" aria-label={project.githubLabel}>
                        {project.linkLabel}
                      </span>
                    )}
                    <a href="#contact" className="project-link">Discuss a build</a>
                  </div>
                </div>

                {project.image && (
                  <a
                    href={project.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`project-thumb${project.isLogo ? " project-thumb--logo" : ""}`}
                    aria-hidden="true"
                    tabIndex={-1}
                  >
                    <img src={project.image} alt="" loading="lazy" />
                  </a>
                )}
              </article>
            ))}
          </div>
        </FadeReveal>
      </div>
    </section>
  );
};

export default Projects;
